import {
  canCosignAsDistinctReviewer,
  canCosignNote,
  canSignNote,
  decide,
  type Grants,
  type NoteAttestationSubject,
} from '@emr/shared';

/**
 * Whether the sign/co-sign buttons on one note appear at all, and if they do, whether they are
 * clickable — the note-composer's own instance of `claude-kit/rules/invariants.md`'s "Business
 * rules live in pure, tested modules — never inline in a component."
 *
 * Combines two things that are each already correct on their own but answer different questions:
 * `decide(grants, 'note:sign' | 'note:cosign')` is the *role* gate (`shell/navigation.ts` and
 * `routes/GatedPages.tsx` apply the identical function to a nav item and a whole route); `@emr/shared`'s
 * `canSignNote`/`canCosignNote`/`canCosignAsDistinctReviewer` (`shared/src/noteAttestation.ts`) are
 * the *note-state* rules — draft vs. signed, author vs. reviewer — that still apply once the role
 * gate has already passed. Neither function is re-derived here; this module only decides which of
 * the three outcomes below the combination produces.
 *
 * Three outcomes, matching `shell/navigation.ts`'s own `NavAvailability` shape for the same reason
 * that file states: a role that can never sign or co-sign a note (a front-desk or billing role,
 * which holds neither capability) gets `hidden` — showing the button disabled with a state-based
 * reason would be noise, since no note's state could ever make it clickable for them. A role that
 * *can* sign or co-sign in general, but not this particular note right now, gets `disabled` with
 * the reason — a physician looking at their own unsigned draft should see why the sign button on
 * *someone else's* note is greyed out, not have it vanish as if the feature does not exist.
 */
export type NoteActionState =
  | { readonly kind: 'hidden' }
  | { readonly kind: 'disabled'; readonly reason: string }
  | { readonly kind: 'available' };

/**
 * May `callerId` sign this note right now?
 *
 * `canSignNote` alone conflates two different reasons a draft is unsignable by this caller — it is
 * already signed, or someone else wrote it — into one boolean; the `reason` text here tells them
 * apart because they call for different next actions (nothing to do vs. "ask the author").
 */
export function signAvailability(
  note: NoteAttestationSubject,
  grants: Grants,
  callerId: string,
): NoteActionState {
  if (!decide(grants, 'note:sign')) return { kind: 'hidden' };
  if (canSignNote(note, callerId)) return { kind: 'available' };
  if (note.status !== 'draft') {
    return { kind: 'disabled', reason: 'This note is already signed.' };
  }
  return { kind: 'disabled', reason: 'Only the note’s own author may sign it.' };
}

/** May `callerId` co-sign this note right now? Physician-only by role; see `noteAttestation.ts`. */
export function cosignAvailability(
  note: NoteAttestationSubject,
  grants: Grants,
  callerId: string,
): NoteActionState {
  if (!decide(grants, 'note:cosign')) return { kind: 'hidden' };
  if (!canCosignNote(note)) {
    return { kind: 'disabled', reason: 'This note has not been signed yet.' };
  }
  if (!canCosignAsDistinctReviewer(note, callerId)) {
    return {
      kind: 'disabled',
      reason: 'You already signed this note. Co-signing needs a second, distinct reviewer.',
    };
  }
  return { kind: 'available' };
}
